import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { PropsWithChildren } from "react";
import { useModal } from "@/hooks/useModal";
import { UpdateModal } from "@/components/Modals/UpdateModal";
import { hardReload } from "@/lib/reload";
import versionData from "@/generated/app-version.json";

/**
 * Detects when a newer build has been deployed while this (cached) bundle is running.
 *  - CURRENT_VERSION is baked in at build (app-version.json, written from the git tag).
 *  - GET /api/v1/version (uncached) reports the live deploy. A cached client vs the
 *    fresh server differ only on a real update.
 * Checks once on mount, skipped in __DEV__.
 */
const CURRENT_VERSION = (versionData.version || "dev").replace(/^v/, "");

/** Newer build? Compares major.minor.patch; a differing build suffix counts as newer. */
function isNewer(latest: string, current: string): boolean {
  const parse = (v: string): number[] => {
    const m = v.match(/^(\d+)\.(\d+)\.(\d+)/);
    return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : [0, 0, 0];
  };
  const a = parse(latest);
  const b = parse(current);
  for (let i = 0; i < 3; i++) if (a[i] !== b[i]) return a[i] > b[i];
  return latest !== current;
}

interface UpdateContextValue {
  currentVersion: string;
  latestVersion: string | null;
  updateAvailable: boolean;
  checkForUpdate: () => Promise<void>;
}

const UpdateContext = createContext<UpdateContextValue | undefined>(undefined);

export function useUpdate() {
  const ctx = useContext(UpdateContext);
  if (!ctx) throw new Error("useUpdate must be used within an UpdateProvider");
  return ctx;
}

export function UpdateProvider({ children }: PropsWithChildren) {
  const { openModal, closeModal } = useModal();
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const dismissedRef = useRef<string | null>(null);

  const checkForUpdate = useCallback(async () => {
    try {
      const res = await fetch("/api/v1/version", { cache: "no-store" });
      if (!res.ok) return;
      const data = (await res.json()) as { version?: string };
      const latest = (data.version ?? "").replace(/^v/, "");
      if (!latest) return;
      setLatestVersion(latest);
      if (isNewer(latest, CURRENT_VERSION) && latest !== dismissedRef.current) {
        openModal(
          <UpdateModal
            current={CURRENT_VERSION}
            latest={latest}
            onLater={() => {
              dismissedRef.current = latest;
              closeModal();
            }}
            onRefresh={() => hardReload()}
          />,
        );
      }
    } catch {
      // Best-effort: network/parse failures are non-fatal.
    }
  }, [openModal, closeModal]);

  // Strip the cache-bust param left by a previous hardReload.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (url.searchParams.has("_reload")) {
      url.searchParams.delete("_reload");
      window.history.replaceState(null, "", url.toString());
    }
  }, []);

  useEffect(() => {
    if (__DEV__) return;
    void checkForUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <UpdateContext.Provider
      value={{
        currentVersion: CURRENT_VERSION,
        latestVersion,
        updateAvailable: latestVersion !== null && isNewer(latestVersion, CURRENT_VERSION),
        checkForUpdate,
      }}
    >
      {children}
    </UpdateContext.Provider>
  );
}
